import { useEffect, useState } from "react";
import {
    Combobox,
    Portal,
    useFilter,
    useListCollection,
} from "@chakra-ui/react";
import { useWebHelper } from "@viabl/echo-web-client-core";

interface IBundle {
    id: string
    name: string
};

interface IProps {
    setParentIdState: Function
    setParentNameState: Function
    initialId: string | undefined
    initialName: string | undefined
}
export function BundlesCombobox(props: IProps) {
    const webHelpers = useWebHelper()

    const [query, setQuery] = useState(props.initialName ?? "");
    const [bundles, setBundles] = useState<IBundle[]>([]);
    const [loading, setLoading] = useState(false);

    const { contains } = useFilter({ sensitivity: "base" });

    const { collection, set } = useListCollection<IBundle>({
        initialItems: [],
        itemToString: (item) => item.name,
        itemToValue: (item) => item.id,
        filter: contains,
    });

    useEffect(() => {
        setQuery(props.initialName ?? "");
    }, [props.initialName]);

    useEffect(() => {
        if (!query.trim()) {
            set([]);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        const timeout = setTimeout(async () => {
            try {
                await webHelpers.GetAsync('/api/rolebundles/search?query=' + encodeURIComponent(query), 'auth').then((response: any) => {
                    const data: IBundle[] = response.results
                    setBundles(data);
                    set(data);
                })

            } catch (error) {
                if ((error as Error).name !== "AbortError") {
                    console.error(error);
                    setBundles([]);
                    set([]);
                }
            } finally {
                setLoading(false);
            }
        }, 200);

        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [query, set, webHelpers]);

    const setParentValue = (nextId: string | null | undefined, nextName: string | undefined) => {
        try {
            props.setParentIdState(nextId ?? null);
        } catch (e) {
            // ignore if parent callback is not provided
        }

        try {
            props.setParentNameState(nextName ?? "");
        } catch (e) {
            // ignore if parent callback is not provided
        }
    };

    return (
        <Combobox.Root
            collection={collection}
            value={props.initialId ? [props.initialId] : []}
            onValueChange={(details) => {
                const selectedBundle = details.items[0];

                if (!selectedBundle) {
                    setParentValue(null, "");
                    set([]);
                    setBundles([]);
                    setQuery("");
                    return;
                }

                const nextName = selectedBundle.name;
                setParentValue(selectedBundle.id, nextName);
                setQuery(nextName);
            }}
            onInputValueChange={(details) => {
                const nextValue = details.inputValue;
                setQuery(nextValue);

                if (details.reason === "item-select") {
                    return;
                }

                setParentValue(null, nextValue);

                if (!nextValue.trim()) {
                    set([]);
                    setBundles([]);
                }
            }}
        >

            <Combobox.Control>
                <Combobox.Input
                    placeholder="Search Bundles..."
                    value={query}
                />
                <Combobox.IndicatorGroup>
                    <Combobox.ClearTrigger onClick={() => {
                        setQuery("");
                        setParentValue(null, "");
                        set([]);
                        setBundles([]);
                    }} />
                    <Combobox.Trigger />
                </Combobox.IndicatorGroup>
            </Combobox.Control>

            <Portal>
                <Combobox.Positioner>
                    <Combobox.Content>
                        {loading && (
                            <Combobox.Empty>
                                Searching...
                            </Combobox.Empty>
                        )}

                        {!loading && collection.items.length === 0 && query && (
                            <Combobox.Empty>
                                No bundles found
                            </Combobox.Empty>
                        )}

                        {!loading &&
                            collection.items.map((bundle) => (
                                <Combobox.Item
                                    item={bundle}
                                    key={bundle.id}
                                    onClick={() => {
                                            setParentValue(bundle.id, bundle.name);
                                            setQuery(bundle.name);
                                        }}
                                >
                                    {bundle.name}
                                    <Combobox.ItemIndicator />
                                </Combobox.Item>
                            ))}
                    </Combobox.Content>
                </Combobox.Positioner>
            </Portal>
        </Combobox.Root>
    );
}